import { useState, useEffect, useRef } from 'react';
import { ShieldAlert, Zap, Crosshair, Radio, Wifi, WifiOff, Cpu, Target, AlertTriangle, CheckCircle, Clock, ChevronRight, Activity, ExternalLink } from 'lucide-react';
import { axios } from '../App';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const SOFT_KILL_INFO = {
  rf_jamming: { label: 'RF Jamming', icon: Radio, color: 'text-yellow-400', desc: 'Disrupts control link between drone and operator' },
  gps_spoofing: { label: 'GPS Spoofing', icon: Wifi, color: 'text-blue-400', desc: 'Feeds false GPS data to redirect drone' },
  comm_disruption: { label: 'Comm Disruption', icon: WifiOff, color: 'text-orange-400', desc: 'Blocks all communication channels' },
  cyber_takeover: { label: 'Cyber Takeover', icon: Cpu, color: 'text-purple-400', desc: 'Attempts to hijack drone control systems' },
};

const HARD_KILL_INFO = {
  directed_energy: { label: 'Directed Energy Weapon (DEW)', color: 'text-red-500', desc: 'High-energy laser to disable/destroy' },
  anti_drone_gun: { label: 'Anti Drone Gun', color: 'text-amber-500', desc: 'Rapid-fire kinetic projectile / autocannon' },
  kinetic_interceptor: { label: 'Kinetic Interceptor', color: 'text-orange-500', desc: 'Physical projectile intercept' },
  net_capture: { label: 'Net Capture', color: 'text-green-500', desc: 'Drone capture net deployment' },
  anti_drone_missile: { label: 'Anti-Drone Missile', color: 'text-red-600', desc: 'Guided missile intercept' },
};

export default function MitigationDecision() {
  const [threatInput, setThreatInput] = useState({
    source: 'manual', drone_id: '', drone_type: '', threat_level: 'high',
    domain: 'air', speed: '', altitude: '', distance: '',
    heading: '', payload_type: '', additional_info: ''
  });
  const [activeDetections, setActiveDetections] = useState([]);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [deployingAction, setDeployingAction] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [autoEngage, setAutoEngage] = useState(false);
  const autoEngageRef = useRef(false);
  const handledDronesRef = useRef(new Set());
  const autoTimerRef = useRef(null);

  useEffect(() => { autoEngageRef.current = autoEngage; }, [autoEngage]);

  useEffect(() => {
    fetchActiveDetections();
    fetchHistory();
  }, []);

  const fetchActiveDetections = async () => {
    try {
      const res = await axios.get('/drones');
      setActiveDetections(res.data.filter(d => d.status === 'active'));
    } catch { /* silent */ }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get('/mitigation/history');
      setHistory(res.data);
    } catch { /* silent */ }
    finally { setLoadingHistory(false); }
  };

  const handleDetectionSelect = (detection) => {
    setThreatInput({
      source: 'detection', drone_id: detection.drone_id,
      drone_type: detection.drone_type || '', threat_level: detection.threat_level || 'high',
      domain: detection.domain || 'air', speed: String(detection.speed || ''),
      altitude: String(detection.altitude || ''), distance: '',
      heading: String(detection.heading || ''), payload_type: '', additional_info: ''
    });
  };

  const analyzeThreat = async () => {
    if (!threatInput.drone_type && !threatInput.additional_info && threatInput.source === 'manual') {
      toast.error('Provide at least drone type or additional info');
      return;
    }
    setIsAnalyzing(true);
    setAnalysisResult(null);
    try {
      const res = await axios.post('/mitigation/analyze', threatInput);
      setAnalysisResult(res.data);
      toast.success('Mitigation analysis complete');
      fetchHistory();
    } catch (err) {
      toast.error('Analysis failed: ' + (err.response?.data?.detail || err.message));
    } finally { setIsAnalyzing(false); }
  };

  const deploySoftKill = async () => {
    if (!analysisResult?.decision_id) return;
    setDeployingAction('soft_kill');
    try {
      await axios.post(`/mitigation/deploy-soft-kill?decision_id=${analysisResult.decision_id}`);
      toast.success('Soft kill deployed successfully');
      setAnalysisResult(prev => ({ ...prev, softKillDeployed: true }));
      fetchHistory();
    } catch (err) {
      toast.error('Deploy failed: ' + (err.response?.data?.detail || err.message));
    } finally { setDeployingAction(null); }
  };

  const authorizeHardKill = async (actionType) => {
    if (!analysisResult?.decision_id) return;
    setDeployingAction(actionType);
    try {
      await axios.post(`/mitigation/authorize-hard-kill?decision_id=${analysisResult.decision_id}&action_type=${actionType}`);
      toast.success(`Hard kill authorized: ${actionType.replace(/_/g, ' ')}`);
      setAnalysisResult(prev => ({ ...prev, hardKillAuthorized: actionType }));
      fetchHistory();
    } catch (err) {
      toast.error('Authorization failed');
    } finally { setDeployingAction(null); }
  };

  // Auto-engage pipeline: poll detections, auto-analyze + auto-deploy soft kill
  const runAutoCycle = async () => {
    if (!autoEngageRef.current) return;
    try {
      const res = await axios.get('/drones');
      const fresh = res.data.find(d => d.status === 'active' && !handledDronesRef.current.has(d.drone_id));
      if (!fresh) return;
      handledDronesRef.current.add(fresh.drone_id);

      // Auto-fill input from detection
      const auto_input = {
        source: 'auto_engage', drone_id: fresh.drone_id,
        drone_type: fresh.drone_type || '', threat_level: fresh.threat_level || 'high',
        domain: fresh.domain || 'air', speed: String(fresh.speed ?? ''),
        altitude: String(fresh.altitude ?? ''), distance: '',
        heading: String(fresh.heading ?? ''), payload_type: '',
        additional_info: 'Auto-engagement pipeline'
      };
      setThreatInput(auto_input);
      toast.info(`AUTO-ENGAGE: analyzing ${fresh.drone_id}`);

      const analyzeRes = await axios.post('/mitigation/analyze', auto_input);
      setAnalysisResult(analyzeRes.data);
      fetchHistory();

      // Auto-deploy soft kill if available
      if (analyzeRes.data?.decision_id && analyzeRes.data?.soft_kill?.recommended) {
        try {
          await axios.post(`/mitigation/deploy-soft-kill?decision_id=${analyzeRes.data.decision_id}`);
          setAnalysisResult(prev => ({ ...prev, softKillDeployed: true }));
          toast.success(`AUTO-ENGAGE: soft kill (${analyzeRes.data.soft_kill.recommended}) deployed — operator action required for hard kill`);
        } catch {
          toast.error('AUTO-ENGAGE: soft kill deploy failed');
        }
      }
    } catch {
      // silent
    }
  };

  useEffect(() => {
    if (autoEngage) {
      runAutoCycle();
      autoTimerRef.current = setInterval(runAutoCycle, 6000);
    } else if (autoTimerRef.current) {
      clearInterval(autoTimerRef.current);
      autoTimerRef.current = null;
    }
    return () => {
      if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEngage]);

  const getUrgencyColor = (u) => {
    switch (u) {
      case 'critical': return 'text-red-500 bg-red-500/20 border-red-500';
      case 'high': return 'text-orange-500 bg-orange-500/20 border-orange-500';
      case 'medium': return 'text-yellow-500 bg-yellow-500/20 border-yellow-500';
      default: return 'text-green-500 bg-green-500/20 border-green-500';
    }
  };

  const getEffColor = (v) => v >= 80 ? 'bg-green-500' : v >= 60 ? 'bg-yellow-500' : v >= 40 ? 'bg-orange-500' : 'bg-red-500';

  return (
    <div className="h-full flex flex-col bg-zinc-950 overflow-hidden" data-testid="mitigation-page">
      {/* Header */}
      <div className="bg-zinc-900/50 border-b border-zinc-800 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight uppercase flex items-center gap-2">
              <ShieldAlert size={24} className="text-red-500" />
              Mitigation Decision Engine
            </h1>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
              AI-Powered Countermeasure Selection | Soft Kill Auto-Deploy | Hard Kill Authorization
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono">
            <button
              onClick={() => {
                setAutoEngage(v => !v);
                if (autoEngage) handledDronesRef.current = new Set();
              }}
              className={`px-3 py-1.5 rounded border-2 font-mono uppercase transition-all flex items-center gap-1.5 ${
                autoEngage
                  ? 'bg-red-500/20 text-red-300 border-red-500 shadow-[0_0_12px_rgba(239,68,68,0.35)]'
                  : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-emerald-600 hover:text-emerald-300'
              }`}
              data-testid="mitigation-auto-engage-toggle"
            >
              <Activity size={12} />
              {autoEngage ? 'AUTO ENGAGE: ARMED' : 'AUTO ENGAGE: OFF'}
            </button>
            <Link
              to="/auto-engage"
              className="px-2 py-1.5 rounded border border-zinc-700 bg-zinc-800 text-zinc-400 hover:text-sky-300 hover:border-sky-600 transition-all flex items-center gap-1"
              data-testid="goto-auto-engage"
            >
              <ExternalLink size={11} /> Live Console
            </Link>
            {analysisResult?.urgency && (
              <span className={`px-3 py-1 rounded border font-bold uppercase ${getUrgencyColor(analysisResult.urgency)}`}
                data-testid="urgency-badge">
                {analysisResult.urgency} Urgency
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Threat Input & Analysis */}
        <div className="w-[55%] border-r border-zinc-800 flex flex-col overflow-hidden">
          {/* Threat Input */}
          <div className="p-4 border-b border-zinc-800 overflow-y-auto max-h-[45%]">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
              <Target size={16} className="text-red-500" />
              Threat Input
            </h3>

            {/* Active Detections Quick Select */}
            {activeDetections.length > 0 && (
              <div className="mb-3">
                <label className="block text-xs text-zinc-400 mb-1.5">Active Detections</label>
                <div className="flex flex-wrap gap-2">
                  {activeDetections.slice(0, 6).map(d => (
                    <button key={d.drone_id} onClick={() => handleDetectionSelect(d)}
                      className={`px-2 py-1 text-xs font-mono rounded border transition-colors ${
                        threatInput.drone_id === d.drone_id
                          ? 'bg-red-500/20 border-red-500 text-red-400'
                          : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                      }`} data-testid={`detection-${d.drone_id}`}>
                      {d.drone_id} ({d.threat_level})
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Drone Type</label>
                <input type="text" value={threatInput.drone_type}
                  onChange={e => setThreatInput(p => ({ ...p, drone_type: e.target.value }))}
                  placeholder="e.g., Quadcopter, FPV"
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-red-500"
                  data-testid="input-drone-type" />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Threat Level</label>
                <select value={threatInput.threat_level}
                  onChange={e => setThreatInput(p => ({ ...p, threat_level: e.target.value }))}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white focus:outline-none focus:border-red-500"
                  data-testid="input-threat-level">
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Domain</label>
                <select value={threatInput.domain}
                  onChange={e => setThreatInput(p => ({ ...p, domain: e.target.value }))}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white focus:outline-none focus:border-red-500"
                  data-testid="input-domain">
                  <option value="air">Air</option>
                  <option value="surface">Surface</option>
                  <option value="underwater">Underwater</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Speed (km/h)</label>
                <input type="text" value={threatInput.speed}
                  onChange={e => setThreatInput(p => ({ ...p, speed: e.target.value }))}
                  placeholder="e.g., 120"
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-red-500"
                  data-testid="input-speed" />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Altitude (m)</label>
                <input type="text" value={threatInput.altitude}
                  onChange={e => setThreatInput(p => ({ ...p, altitude: e.target.value }))}
                  placeholder="e.g., 200"
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-red-500"
                  data-testid="input-altitude" />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Distance (km)</label>
                <input type="text" value={threatInput.distance}
                  onChange={e => setThreatInput(p => ({ ...p, distance: e.target.value }))}
                  placeholder="e.g., 3.5"
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-red-500"
                  data-testid="input-distance" />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Payload Type</label>
                <input type="text" value={threatInput.payload_type}
                  onChange={e => setThreatInput(p => ({ ...p, payload_type: e.target.value }))}
                  placeholder="e.g., Camera, Explosive"
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-red-500"
                  data-testid="input-payload" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-zinc-400 mb-1">Additional Intel</label>
                <input type="text" value={threatInput.additional_info}
                  onChange={e => setThreatInput(p => ({ ...p, additional_info: e.target.value }))}
                  placeholder="e.g., approaching from NW, possible swarm lead"
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-red-500"
                  data-testid="input-additional" />
              </div>
            </div>

            <button onClick={analyzeThreat} disabled={isAnalyzing}
              className="w-full mt-4 px-4 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed rounded font-mono text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
              data-testid="analyze-btn">
              {isAnalyzing ? (<><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Analyzing Threat...</>)
                : (<><ShieldAlert size={18} /> Analyze &amp; Recommend Countermeasures</>)}
            </button>
          </div>

          {/* Analysis Result */}
          <div className="flex-1 p-4 overflow-y-auto">
            {!analysisResult && !isAnalyzing && (
              <div className="text-center py-12 text-zinc-500">
                <ShieldAlert size={48} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">Input threat data and click analyze for AI countermeasure recommendations</p>
              </div>
            )}

            {analysisResult && (
              <div className="space-y-4">
                {/* AI Assessment */}
                <div className="bg-zinc-900 border border-zinc-800 rounded p-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2 flex items-center gap-2">
                    <Cpu size={14} className="text-sky-500" /> AI Threat Assessment
                  </h4>
                  <p className="text-sm text-zinc-200" data-testid="ai-assessment">{analysisResult.assessment}</p>
                </div>

                {/* Soft Kill - Auto Deploy */}
                <div className={`border rounded p-4 ${analysisResult.softKillDeployed ? 'bg-green-900/20 border-green-500/50' : 'bg-zinc-900 border-zinc-800'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-yellow-400 flex items-center gap-2">
                      <Zap size={14} /> Soft Kill — Auto Deploy
                    </h4>
                    {analysisResult.softKillDeployed && (
                      <span className="flex items-center gap-1 text-xs text-green-400 font-mono" data-testid="soft-kill-deployed">
                        <CheckCircle size={14} /> DEPLOYED
                      </span>
                    )}
                  </div>

                  {analysisResult.soft_kill && (() => {
                    const sk = analysisResult.soft_kill;
                    const info = SOFT_KILL_INFO[sk.recommended] || {};
                    const Icon = info.icon || Radio;
                    return (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded bg-zinc-800 ${info.color || 'text-yellow-400'}`}>
                            <Icon size={20} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-white text-sm">{info.label || sk.recommended}</span>
                              <span className="text-xs font-mono text-purple-400">{sk.confidence}% confidence</span>
                            </div>
                            <p className="text-xs text-zinc-400">{sk.reasoning}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-zinc-500">Effectiveness:</span>
                          <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                            <div className={`h-full ${getEffColor(sk.estimated_effectiveness)} transition-all duration-500`}
                              style={{ width: `${sk.estimated_effectiveness}%` }} />
                          </div>
                          <span className="font-mono text-zinc-300">{sk.estimated_effectiveness}%</span>
                        </div>
                        {!analysisResult.softKillDeployed && (
                          <button onClick={deploySoftKill} disabled={deployingAction === 'soft_kill'}
                            className="w-full py-2.5 bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-800 rounded font-mono text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
                            data-testid="deploy-soft-kill-btn">
                            {deployingAction === 'soft_kill'
                              ? <><div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" /> Deploying...</>
                              : <><Zap size={14} /> Deploy {info.label}</>}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Hard Kill Options */}
                <div className="bg-zinc-900 border border-zinc-800 rounded p-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-red-400 mb-3 flex items-center gap-2">
                    <Crosshair size={14} /> Hard Kill — Operator Authorization Required
                  </h4>
                  <div className="space-y-2">
                    {(analysisResult.hard_kill_options || []).map((opt, i) => {
                      const info = HARD_KILL_INFO[opt.type] || {};
                      const isAuthorized = analysisResult.hardKillAuthorized === opt.type;
                      return (
                        <div key={opt.type} className={`border rounded p-3 transition-all ${
                          isAuthorized ? 'bg-red-900/20 border-red-500/50' : 'border-zinc-700 hover:border-zinc-600'
                        }`} data-testid={`hard-kill-${opt.type}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className={`text-lg font-bold font-mono ${i === 0 ? 'text-green-400' : 'text-zinc-500'}`}>
                                #{opt.rank}
                              </span>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className={`font-bold text-sm ${info.color || 'text-white'}`}>{info.label || opt.type}</span>
                                  <span className="text-xs font-mono text-purple-400">{opt.confidence}%</span>
                                  {opt.collateral_risk && (
                                    <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${
                                      opt.collateral_risk === 'high' ? 'text-red-400 bg-red-500/10 border-red-500/30'
                                      : opt.collateral_risk === 'medium' ? 'text-orange-400 bg-orange-500/10 border-orange-500/30'
                                      : 'text-green-400 bg-green-500/10 border-green-500/30'
                                    }`}>
                                      {opt.collateral_risk} risk
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-zinc-400 mt-0.5">{opt.reasoning}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right text-xs">
                                <div className="text-zinc-500">Effectiveness</div>
                                <div className="font-mono text-zinc-200">{opt.estimated_effectiveness}%</div>
                              </div>
                              {isAuthorized ? (
                                <span className="flex items-center gap-1 text-xs text-red-400 font-mono px-3 py-1.5 bg-red-500/20 rounded border border-red-500/30" data-testid={`authorized-${opt.type}`}>
                                  <CheckCircle size={12} /> AUTHORIZED
                                </span>
                              ) : !analysisResult.hardKillAuthorized ? (
                                <button onClick={() => authorizeHardKill(opt.type)}
                                  disabled={deployingAction === opt.type}
                                  className="px-3 py-1.5 bg-red-600/80 hover:bg-red-600 disabled:bg-red-800 rounded text-xs font-mono uppercase tracking-wider transition-colors flex items-center gap-1"
                                  data-testid={`authorize-${opt.type}`}>
                                  {deployingAction === opt.type
                                    ? <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                                    : <><Crosshair size={12} /> Authorize</>}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Mitigation History */}
        <div className="w-[45%] flex flex-col overflow-hidden">
          <div className="p-4 border-b border-zinc-800">
            <h3 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
              <Clock size={16} className="text-zinc-400" />
              Mitigation History
            </h3>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {loadingHistory ? (
              <div className="text-center py-8 text-zinc-500 text-sm">Loading history...</div>
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 text-sm">No mitigation decisions yet</div>
            ) : (
              <div className="space-y-2">
                {history.map(h => (
                  <div key={h.id} className="bg-zinc-900 border border-zinc-800 rounded p-3" data-testid={`history-${h.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          h.status === 'deployed' ? 'bg-green-500'
                          : h.status === 'authorized' ? 'bg-red-500'
                          : h.status === 'analyzed' ? 'bg-yellow-500'
                          : 'bg-zinc-500'
                        }`} />
                        <span className="text-xs font-mono uppercase text-zinc-300">
                          {h.status === 'deployed' ? 'Soft Kill Deployed'
                            : h.status === 'authorized' ? 'Hard Kill Authorized'
                            : 'Analyzed'}
                        </span>
                      </div>
                      <span className="text-[10px] font-mono text-zinc-500">
                        {h.created_at ? new Date(h.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-400">
                      <span className="text-zinc-500">Threat:</span>{' '}
                      {h.threat_input?.drone_type || 'Unknown'} ({h.threat_input?.threat_level || '?'}) — {h.threat_input?.domain || 'air'}
                    </div>
                    {h.selected_action && (
                      <div className="mt-1 flex items-center gap-1 text-xs">
                        <ChevronRight size={12} className="text-zinc-600" />
                        <span className={h.action_type === 'hard_kill' ? 'text-red-400' : 'text-yellow-400'}>
                          {h.selected_action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                        <span className="text-zinc-600">({h.action_type === 'hard_kill' ? 'Hard Kill' : 'Soft Kill'})</span>
                      </div>
                    )}
                    {h.ai_assessment && (
                      <p className="text-[11px] text-zinc-500 mt-1 line-clamp-2">{h.ai_assessment}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
